import type { AddComponentDto, AddItemDto, AddItemOptionDto, CartAdditionOption } from '../../types/cart';
import type { MenuSectionDto } from '../../generated-api/models/MenuSectionDto';
import type { MenuItemVariantDto } from '../../generated-api/models/MenuItemVariantDto';
import type { MenuBuilderComponentDto } from '../../generated-api/models/MenuBuilderComponentDto';
import type { OptionDto } from '../../generated-api/models/OptionDto';
import Big from 'big.js';
import type {
  CartDto,
  DeliveryMethodDto,
  DiscountDto,
  OrderLineDto,
  OrderLineOptionDto,
  PayCartDto,
  PaymentMethodDto,
} from '../../generated-api';

export const itemIsEqualToOrderLine = (item: AddItemDto, orderLine: OrderLineDto) => {
  if (item.itemVariantId === orderLine.itemVariantId) {
    return (
      item.comment === orderLine.comment &&
      item.options.length === orderLine.options.length &&
      item.options.every((option) => existIn(option, orderLine.options))
    );
  }

  return false;
};

const existIn = (additionOption: AddItemOptionDto, orderLineOptions: OrderLineOptionDto[]) =>
  orderLineOptions.some((olOption) => isEqualOption(additionOption, olOption));

const isEqualOption = (option: AddItemOptionDto, orderLineOption: OrderLineOptionDto) =>
  orderLineOption.itemVariantId === option.itemVariantId && orderLineOption.quantity === option.quantity;

export const isEqualNotOptionId = (option?: OptionDto) => (cartOption: CartAdditionOption) =>
  cartOption.option.optionId !== option?.optionId;
export const isEqualOptionId = (option?: OptionDto) => (cartOption: CartAdditionOption) =>
  cartOption.option.optionId === option?.optionId;

export const getAllItemsFromSections = (sections: MenuSectionDto[]): MenuItemVariantDto[] =>
  sections.flatMap((section) => [...section.items, ...section.subsections.flatMap((sub) => sub.items)]);

export const findMenuItemByOrderLine = (
  orderLine: OrderLineDto,
  sections: MenuSectionDto[]
): MenuItemVariantDto | undefined =>
  getAllItemsFromSections(sections).find((item) => item.variantLinkId === orderLine.itemVariantId);

export const isCartAdditionOption = (option: unknown): option is CartAdditionOption =>
  Boolean(option && (option as CartAdditionOption).option && (option as CartAdditionOption).type);

export const calcTotalServiceCharge = (price: number, serviceCharge?: number) =>
  serviceCharge ? Number(new Big(serviceCharge).div(100).times(price)) : 0;

interface PayCartInfo {
  paymentMethod: PaymentMethodDto;
  deliveryMethod: DeliveryMethodDto;
  deviceId: string;
  tableNumber?: string;
  tipAmount: number;
  comment: string | undefined;
  placementId?: number;
  discount?: DiscountDto;
  dynamicFields?: { [key: string]: string };
  giftCardPhoneNumber?: string;
  vatType: 'EAT_IN' | 'TAKEAWAY';
}

export const buildAddComponentDto = (
  componentDto: MenuBuilderComponentDto,
  allProducts: MenuItemVariantDto[],
  componentCustomizations: Map<string, CartAdditionOption[]>,
  parentMenuSectionId?: string,
  parentSectionProductId?: string
): AddComponentDto => {
  const resolvedItem = allProducts.find((p) => p.variantLinkId === componentDto.productSizeId);
  const options = componentCustomizations.get(componentDto.productSizeId) ?? [];

  const subComponents =
    resolvedItem?.isComposite && resolvedItem.components
      ? resolvedItem.components.map((sub) =>
          buildAddComponentDto(sub, allProducts, componentCustomizations, parentMenuSectionId, parentSectionProductId)
        )
      : undefined;

  return {
    components: subComponents,
    courseId: resolvedItem?.courseId,
    itemVariantId: componentDto.productSizeId,
    menuSectionId: parentMenuSectionId,
    name: componentDto.productName,
    options: options.map((opt) => ({
      name: opt.option.name,
      itemVariantId: opt.option.optionId,
      type: opt.type,
      quantity: opt.quantity,
      price: opt.option.price,
      takeawayPrice: opt.option.takeawayPrice,
    })),
    price: componentDto.effectivePrice,
    sectionProductId: parentSectionProductId,
    takeawayPrice: componentDto.effectiveTakeawayPrice,
    unitQuantity: componentDto.quantity,
  };
};

export const toPayCartDto = (payCartInfo: PayCartInfo, cart: CartDto): PayCartDto => ({
  cart,
  comment: payCartInfo.comment || undefined,
  deliveryMethodId: payCartInfo.deliveryMethod.id,
  deliveryPoint: {
    primaryReference: payCartInfo.tableNumber || payCartInfo.deliveryMethod.primaryReference || '',
  },
  deviceId: payCartInfo.deviceId,
  discountCode: payCartInfo.discount?.discountCodes[0],
  dynamicFields: payCartInfo.dynamicFields,
  giftCardPhoneNumber: payCartInfo.giftCardPhoneNumber,
  paymentMethod: payCartInfo.paymentMethod,
  placementId: payCartInfo.placementId,
  tipAmount: payCartInfo.tipAmount,
  vatType: payCartInfo.vatType,
});
